import { BaseService, ErrorResponse } from "../base";
import type { Abfallart, Auflagenvorlage, ConfigurationItem, Einheit, Einsatzstoff, Produktionsrueckstand, Energietraeger, Emissionskategorie, Navigation, Heizgradtage, Checkliste, CO2Preis, Energiekategorie, Programm, Entsorger, Berichtskonfiguration, NachhaltigkeitsFrage, StandardHandlungsfeldeintrag } from "./model";

export const ConfigurationSymbol = Symbol();

export interface IConfigurationItemRepository<T> {
  List(): Promise<T[]>;
  Patch(item: T): Promise<void>;
  Delete(itemid: string): Promise<void>;
  Kind: string;
}

export class TeilnahmeItemRepository<T> implements IConfigurationItemRepository<T> {
  private _service: BaseService;
  private _slug: string;

  constructor(service: BaseService, slug: string) {
    this._service = service;
    this._slug = slug;
  }

  get Kind(): string {
    return this._slug;
  }

  async List(): Promise<T[]> {
    let result: T[] | ErrorResponse = await this._service.Get<T[]>(this._slug, true);

    if (result instanceof ErrorResponse) {
      console.log(result.message);
      return Array<T>();
    }

    return result;
  }

  async Patch(item: T): Promise<void> {
    let result: T | ErrorResponse = await this._service.Patch(this._slug, item);

    if (result instanceof ErrorResponse) {
      console.log(result.message);
    }
  }

  async Delete(itemid: string): Promise<void> {
    let result: void | ErrorResponse = await this._service.Delete(`${this._slug}/${itemid}`);

    if (result instanceof ErrorResponse) {
      console.log(result.message);
    }
  }
}

export interface IConfigurationService {
  Programme: IConfigurationItemRepository<Programm>;
  Einheiten: IConfigurationItemRepository<Einheit>;
  Abfallarten: IConfigurationItemRepository<Abfallart>;
  Auflagenvorlagen: IConfigurationItemRepository<Auflagenvorlage>;
  Entsorger: IConfigurationItemRepository<Entsorger>;
  Einsatzstoffe: IConfigurationItemRepository<Einsatzstoff>;
  Produktionsrueckstaende: IConfigurationItemRepository<Produktionsrueckstand>;
  Energietraeger: IConfigurationItemRepository<Energietraeger>;
  Emissionskategorien: IConfigurationItemRepository<Emissionskategorie>;
  Energiekategorien: IConfigurationItemRepository<Energiekategorie>;
  Heizgradtage: IConfigurationItemRepository<Heizgradtage>;
  Umweltauswirkungen: IConfigurationItemRepository<ConfigurationItem>;
  Berichtskonfigurationen: IConfigurationItemRepository<Berichtskonfiguration>;
  Checklisten: IConfigurationItemRepository<Checkliste>;
  StandardHandlungsfeldeintraege: IConfigurationItemRepository<StandardHandlungsfeldeintrag>;

  GetNavigation(useCache:boolean): Promise<Navigation>;
  PatchNavigation(navigation:Navigation): Promise<void | ErrorResponse>;

  GetAuditcheckliste(): Promise<Checkliste>;
}

export class ConfigurationService extends BaseService implements IConfigurationService {
  private _navigation: Navigation | undefined;

  constructor(baseUri: string, token: string) {
    super(baseUri, token);
  }

  async GetNavigation(useCache:boolean): Promise<Navigation> {
    if(useCache && this._navigation)
      return this._navigation;

    this._navigation = await this.Get('navigation',true) as Navigation;
    return this._navigation;
  }
  async PatchNavigation(navigation:Navigation): Promise<void | ErrorResponse> {
    return this.Patch('navigation', navigation);
  }
  
  async GetAuditcheckliste(): Promise<Checkliste> {
    return await this.Get('Checkliste', true) as Checkliste;    
  }

  get Programme(): IConfigurationItemRepository<Programm> {
    return new TeilnahmeItemRepository<Programm>(this, 'Programm');
  }

  get Heizgradtage(): IConfigurationItemRepository<Heizgradtage> {
    return new TeilnahmeItemRepository<Heizgradtage>(this, 'Heizgradtage');
  }

  get CO2Preise(): IConfigurationItemRepository<CO2Preis> {
    return new TeilnahmeItemRepository<CO2Preis>(this, 'CO2Preis');
  }

  get Einheiten(): IConfigurationItemRepository<Einheit> {
    return new TeilnahmeItemRepository<Einheit>(this, 'Einheit');
  }

  get Abfallarten(): IConfigurationItemRepository<Abfallart> {
    return new TeilnahmeItemRepository<Abfallart>(this, 'Abfallart');
  }

  get Auflagenvorlagen(): IConfigurationItemRepository<Auflagenvorlage> {
    return new TeilnahmeItemRepository<Auflagenvorlage>(this, 'Auflagenvorlage');
  }

  get Entsorger(): IConfigurationItemRepository<Entsorger> {
    return new TeilnahmeItemRepository<Entsorger>(this, 'Entsorger');
  }

  get Einsatzstoffe(): IConfigurationItemRepository<Einsatzstoff> {
    return new TeilnahmeItemRepository<Einsatzstoff>(this, 'Einsatzstoff');
  }

  get Produktionsrueckstaende(): IConfigurationItemRepository<Produktionsrueckstand> {
    return new TeilnahmeItemRepository<Produktionsrueckstand>(this, 'Produktionsrueckstand');
  }

  get Energietraeger(): IConfigurationItemRepository<Energietraeger> {
    return new TeilnahmeItemRepository<Energietraeger>(this, 'Energietraeger');
  }

  get Emissionskategorien(): IConfigurationItemRepository<Emissionskategorie> {
    return new TeilnahmeItemRepository<Emissionskategorie>(this, 'Emissionskategorie');
  }

  get Energiekategorien(): IConfigurationItemRepository<Energiekategorie> {
    return new TeilnahmeItemRepository<Energiekategorie>(this, 'Energiekategorie');
  }

  get Umweltauswirkungen(): IConfigurationItemRepository<ConfigurationItem> {
    return new TeilnahmeItemRepository<ConfigurationItem>(this, 'Umweltauswirkung');
  }

  get Berichtskonfigurationen(): IConfigurationItemRepository<Berichtskonfiguration> {
    return new TeilnahmeItemRepository<Berichtskonfiguration>(this, 'Berichtskonfiguration');
  }

  get Checklisten(): IConfigurationItemRepository<Checkliste> {
    return new TeilnahmeItemRepository<Checkliste>(this, 'Checkliste');
  }

  get NachhaltigkeitsFragen(): IConfigurationItemRepository<NachhaltigkeitsFrage> {
    return new TeilnahmeItemRepository<NachhaltigkeitsFrage>(this, 'NachhaltigkeitsFrage');
  }

  get StandardHandlungsfeldeintraege(): IConfigurationItemRepository<StandardHandlungsfeldeintrag> {
    return new TeilnahmeItemRepository<StandardHandlungsfeldeintrag>(this, 'StandardHandlungsfeldeintrag');
  }

  async DeleteItem(itemId: string): Promise<void> {
    await this.Delete(itemId);
  }

  async List(kind: string): Promise<ConfigurationItem[]> {
    return await this.Get<ConfigurationItem[]>(kind, true) as ConfigurationItem[];
  }

  async Upsert(item: ConfigurationItem, slug?: string): Promise<void> {
    await this.Patch(slug ?? '', item);
  }
}